import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, ArrowLeft, User, Save, CheckCircle2, AlertCircle, Upload, FileText, Image, Bell } from 'lucide-react';
import { Switch } from '../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({ name: '', phone: '', location: '', state: '', skills: '', work_history: '', education: '', resume_text: '', bio: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const resumeRef = useRef(null);
  const avatarRef = useRef(null);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '', phone: user.phone || '', location: user.location || '',
        state: user.state || '', skills: (user.skills || []).join(', '),
        work_history: user.work_history || '', education: user.education || '',
        resume_text: user.resume_text || '', bio: user.bio || ''
      });
      // Fetch alert preferences
      axios.get(`${API}/profile/alerts`, { withCredentials: true })
        .then(r => setAlertsEnabled(r.data.job_alerts_enabled !== false)).catch(() => {});
    }
  }, [user]);

  const toggleAlerts = async (enabled) => {
    setAlertsEnabled(enabled);
    try {
      await axios.put(`${API}/profile/alerts`, { job_alerts_enabled: enabled }, { withCredentials: true });
    } catch (err) { console.error(err); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSaved(false); setError('');
    try {
      await axios.put(`${API}/profile`, {
        ...form,
        skills: form.skills.split(',').map(s => s.trim()).filter(Boolean)
      }, { withCredentials: true });
      await checkAuth();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true); setUploadMsg('');
    try {
      const endpoint = type === 'resume' ? 'upload/resume' : 'upload/avatar';
      const { data } = await axios.post(`${API}/${endpoint}`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadMsg(`${type === 'resume' ? 'Resume' : 'Avatar'} uploaded successfully!`);
      await checkAuth();
      setTimeout(() => setUploadMsg(''), 3000);
    } catch (err) {
      setUploadMsg(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-[#047857]/10 flex items-center justify-center">
            <User size={24} className="text-[#047857]" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>My Profile</h1>
            <p className="text-sm text-[#1C2A24]/60">Complete your profile for Quick Apply</p>
          </div>
        </div>

        {!user?.profile_complete && (
          <div className="bg-[#D97757]/10 rounded-2xl p-4 mb-6 border border-[#D97757]/20 flex items-center gap-3" data-testid="incomplete-warning">
            <AlertCircle size={18} className="text-[#D97757] shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#1C2A24]/80">Complete your name, phone, location, and skills to enable Quick Apply.</p>
          </div>
        )}

        <form onSubmit={handleSave} className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-5" data-testid="profile-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-[#1C2A24]/70">Full Name *</Label>
              <Input data-testid="profile-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" required />
            </div>
            <div>
              <Label className="text-sm text-[#1C2A24]/70">Phone *</Label>
              <Input data-testid="profile-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" placeholder="(555) 123-4567" required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-[#1C2A24]/70">City/Location *</Label>
              <Input data-testid="profile-location" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" placeholder="City, State" required />
            </div>
            <div>
              <Label className="text-sm text-[#1C2A24]/70">State</Label>
              <Select value={form.state} onValueChange={v => setForm(f => ({ ...f, state: v }))}>
                <SelectTrigger className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" data-testid="profile-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm text-[#1C2A24]/70">Skills * (comma-separated)</Label>
            <Input data-testid="profile-skills" value={form.skills} onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" placeholder="Customer Service, Excel, Communication, Data Entry" required />
          </div>
          <div>
            <Label className="text-sm text-[#1C2A24]/70">Bio</Label>
            <Textarea data-testid="profile-bio" value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={2} placeholder="A brief intro about yourself" />
          </div>
          <div>
            <Label className="text-sm text-[#1C2A24]/70">Work History</Label>
            <Textarea data-testid="profile-work-history" value={form.work_history} onChange={e => setForm(f => ({ ...f, work_history: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={3} placeholder="Describe your work experience..." />
          </div>
          <div>
            <Label className="text-sm text-[#1C2A24]/70">Education</Label>
            <Textarea data-testid="profile-education" value={form.education} onChange={e => setForm(f => ({ ...f, education: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={2} placeholder="Your education background..." />
          </div>
          <div>
            <Label className="text-sm text-[#1C2A24]/70">Resume (paste text)</Label>
            <Textarea data-testid="profile-resume" value={form.resume_text} onChange={e => setForm(f => ({ ...f, resume_text: e.target.value }))} className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] mt-1" rows={4} placeholder="Paste your resume text here for Quick Apply..." />
          </div>

          {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-2xl text-sm" data-testid="profile-error">{error}</div>}
          {saved && <div className="bg-[#A7F3D0]/30 text-[#047857] px-4 py-3 rounded-2xl text-sm flex items-center gap-2" data-testid="profile-saved"><CheckCircle2 size={14} strokeWidth={1.5} /> Profile saved successfully!</div>}

          {/* Job Alerts */}
          <div className="border-t border-[#E5E0D8] pt-5 mt-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell size={18} className="text-[#047857]" strokeWidth={1.5} />
                <div>
                  <Label className="text-sm font-medium">Job Alert Emails</Label>
                  <p className="text-xs text-[#1C2A24]/50">Get emailed when new jobs match your skills (Plus/Pro only)</p>
                </div>
              </div>
              <Switch checked={alertsEnabled} onCheckedChange={toggleAlerts} data-testid="alerts-toggle" />
            </div>
          </div>

          {/* File Uploads */}
          <div className="border-t border-[#E5E0D8] pt-5 mt-2">
            <h3 className="text-base font-medium mb-4" style={{fontFamily:'Outfit'}}>File Uploads</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-[#1C2A24]/70 mb-2 block">Resume (PDF, DOC)</Label>
                <input ref={resumeRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => handleFileUpload(e.target.files[0], 'resume')} />
                <Button type="button" variant="outline" onClick={() => resumeRef.current?.click()} disabled={uploading}
                  className="w-full rounded-2xl border-dashed border-2 border-[#E5E0D8] h-20 flex flex-col items-center justify-center gap-1 hover:border-[#047857] hover:bg-[#F4F1E8]" data-testid="upload-resume-btn">
                  <Upload size={18} className="text-[#047857]" strokeWidth={1.5} />
                  <span className="text-xs text-[#1C2A24]/60">{user?.resume_filename || 'Upload Resume'}</span>
                </Button>
              </div>
              <div>
                <Label className="text-sm text-[#1C2A24]/70 mb-2 block">Profile Photo</Label>
                <input ref={avatarRef} type="file" accept=".jpg,.jpeg,.png,.gif,.webp" className="hidden" onChange={e => handleFileUpload(e.target.files[0], 'avatar')} />
                <Button type="button" variant="outline" onClick={() => avatarRef.current?.click()} disabled={uploading}
                  className="w-full rounded-2xl border-dashed border-2 border-[#E5E0D8] h-20 flex flex-col items-center justify-center gap-1 hover:border-[#047857] hover:bg-[#F4F1E8]" data-testid="upload-avatar-btn">
                  <Image size={18} className="text-[#D97757]" strokeWidth={1.5} />
                  <span className="text-xs text-[#1C2A24]/60">Upload Photo</span>
                </Button>
              </div>
            </div>
            {uploadMsg && <p className="text-sm text-[#047857] mt-2" data-testid="upload-msg">{uploadMsg}</p>}
          </div>

          <Button data-testid="save-profile-btn" type="submit" disabled={saving} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12 px-8">
            {saving ? 'Saving...' : <><Save size={16} className="mr-2" strokeWidth={1.5} /> Save Profile</>}
          </Button>
        </form>
      </div>
    </div>
  );
}
